import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, FlaskConical, ScrollText, Microscope, Boxes, Truck,
  Award, Lock, Beaker, GraduationCap, Users, Sparkles, BookOpen, HelpCircle,
  FileCheck2, ChevronRight, Star,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import ProductCard from "@/components/ProductCard";
import { CATEGORY_INFO } from "@/lib/peptideMeta";

const heroImg =
  "https://images.unsplash.com/photo-1514373941175-0a141072bbc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxkb2clMjBydW5uaW5nJTIwaGVhbHRoeXxlbnwwfHx8fDE3ODQzNTkwMDN8MA&ixlib=rb-4.1.0&q=85";

const TRUST = [
  { icon: FlaskConical, label: "Third-party tested" },
  { icon: FileCheck2, label: "Full documentation" },
  { icon: Beaker, label: "Veterinary focused" },
  { icon: Truck, label: "Fast fulfillment" },
  { icon: Boxes, label: "Batch tracking" },
  { icon: Award, label: "Quality standards" },
];

const WHY = [
  { icon: ScrollText, t: "Research Documentation", d: "COAs, batch records, and full analytical panels attached to every order." },
  { icon: Microscope, t: "Independent Testing", d: "HPLC + Mass Spec by an unaffiliated third-party lab. Purity ≥ 98%." },
  { icon: Award, t: "Quality Standards", d: "USA-sourced synthesis with documented QMS controls and full lot traceability." },
  { icon: Beaker, t: "Veterinary Focus", d: "Every listing is curated for veterinary research applications — never a generic marketplace." },
  { icon: Lock, t: "Secure Ordering", d: "Verified accounts, encrypted sessions, and full order-history transparency in the portal." },
  { icon: GraduationCap, t: "Educational Resources", d: "Structured guides, mechanism notes, and citations for every compound we list." },
];

const RESOURCES = [
  { icon: BookOpen, t: "Research Articles", d: "Deep dives into mechanisms & study results.", to: "/research" },
  { icon: FlaskConical, t: "Peptide Guides", d: "Compound-by-compound handling & storage.", to: "/catalog" },
  { icon: GraduationCap, t: "Veterinary Education", d: "Protocols, dosing frameworks, safety.", to: "/research" },
  { icon: HelpCircle, t: "FAQ", d: "Answers to the most common inquiries.", to: "/faq" },
];

const STATS = [
  { n: "10+", l: "Years of research sourcing" },
  { n: "50k+", l: "Orders fulfilled" },
  { n: "≥ 98%", l: "Minimum purity floor" },
  { n: "24h", l: "Response time" },
];

const REVIEWS = [
  { q: "The documentation is the tightest I've seen in this category — every batch is traceable to the assay.", a: "Dr. R. Chen, DVM · Veterinary Research" },
  { q: "Fast fulfillment and the COA lookup portal makes reconciling shipments trivial.", a: "M. Alvarez · Lab Operations Lead" },
  { q: "Peps4Pets is my default reference for canine peptide sourcing. The bar is high.", a: "Dr. J. Patel · Clinical Investigator" },
];

export default function Home() {
  const [peptides, setPeptides] = useState([]);
  useEffect(() => {
    api.get("/peptides").then((r) => setPeptides(r.data));
  }, []);
  const featured = peptides.filter((p) => p.featured).slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* 2. Full-screen Hero */}
      <section className="relative overflow-hidden" style={{ minHeight: "calc(100vh - 64px)" }}>
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10" />
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" style={{ minHeight: "calc(100vh - 64px)" }}>
          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
            <Badge className="rounded-md bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-200 font-mono text-[10px] uppercase tracking-widest">
              <ShieldCheck className="w-3 h-3 mr-1.5" strokeWidth={2} /> Third-Party Tested · Research Purposes Only
            </Badge>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold text-slate-900 leading-[1.02] tracking-tight">
              Peptides, precisely
              <br />
              <span className="text-blue-600">documented for pets.</span>
            </h1>
            <p className="mt-6 max-w-xl text-slate-700 text-lg leading-relaxed">
              A clinical-grade reference library of veterinary research peptides — indexed by mechanism,
              independently assayed, and shipped with full documentation.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/catalog">
                <Button data-testid="hero-shop-btn" className="h-14 px-7 py-6 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-base">
                  Shop Catalog <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
                </Button>
              </Link>
              <Link to="/login">
                <Button data-testid="hero-create-account-btn" variant="outline" className="h-14 px-7 py-6 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-md font-medium text-base transition-colors">
                  Create Account
                </Button>
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-6 flex-wrap">
              {["BPC-157", "TB-500", "Ipamorelin", "GHK-Cu"].map((c) => (
                <div key={c} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  <span className="font-mono text-xs text-slate-500">{c}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3. Trust Indicators */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {TRUST.map((t) => (
            <div key={t.label} className="flex items-center gap-2.5" data-testid={`trust-${t.label.toLowerCase().replace(/\s/g, "-")}`}>
              <t.icon className="w-5 h-5 text-blue-600 flex-shrink-0" strokeWidth={1.5} />
              <div className="text-sm font-medium text-slate-900">{t.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Featured Categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <SectionHeader eyebrow="01 · Categories" title="Explore by mechanism." description="Every compound in our reference library is indexed by its primary research area." cta="View full catalog" ctaTo="/catalog" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {Object.entries(CATEGORY_INFO).map(([key, c]) => (
            <Link key={key} to="/catalog" data-testid={`category-card-${key.toLowerCase()}`} className="group relative overflow-hidden rounded-md border border-slate-200 bg-white h-72 block card-clinical">
              <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-70 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/40 to-transparent" />
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="font-mono text-[10px] uppercase tracking-widest text-blue-300">{key}</div>
                <div className="mt-1 font-display text-2xl font-semibold">{c.title}</div>
                <p className="mt-2 text-sm text-white/80 max-w-xs">{c.tagline}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-blue-300 group-hover:text-blue-200">
                  Browse compounds <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 5. Featured Products */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <SectionHeader eyebrow="02 · Best Sellers" title="Featured research peptides." description="Our most-requested compounds, each with full analytical documentation." cta="See all products" ctaTo="/catalog" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.slug} peptide={p} />)}
          </div>
        </div>
      </section>

      {/* 6. Why Choose */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <SectionHeader eyebrow="03 · Why Peps4Pets" title="Six things we don't compromise on." description="Documentation-first. Every step of the chain of custody is designed to hold up under audit." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {WHY.map((w) => (
            <div key={w.t} className="border border-slate-200 rounded-md p-6 bg-white card-clinical" data-testid={`why-${w.t.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
                <w.icon className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
              </div>
              <div className="mt-5 font-display text-lg font-semibold text-slate-900">{w.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 7. Educational Resources */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <SectionHeader eyebrow="04 · Resources" title="Learn before you order." description="A structured resource center for researchers and veterinary professionals." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {RESOURCES.map((r) => (
              <Link key={r.t} to={r.to} data-testid={`resource-${r.t.toLowerCase().replace(/\s/g, "-")}`} className="border border-slate-200 rounded-md p-6 bg-white card-clinical block group">
                <r.icon className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                <div className="mt-5 font-display text-lg font-semibold text-slate-900">{r.t}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.d}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-blue-700 group-hover:text-blue-800">
                  Explore <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Quality & Documentation */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] uppercase tracking-widest text-blue-400 mb-3">05 · Quality</div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
              Documentation attached to <span className="text-blue-400">every batch.</span>
            </h2>
            <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-md">
              From synthesis to bench — a fully traceable chain of custody. Each vial ships with a scannable batch
              ID linked to its complete analytical panel.
            </p>
            <Link to="/quality" className="inline-block mt-8">
              <Button className="h-12 px-6 bg-blue-500 hover:bg-blue-400 text-slate-900 rounded-md font-medium">
                Explore quality standards <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { t: "COAs", d: "HPLC purity + Mass Spec identity per batch." },
              { t: "Batch Testing", d: "LAL endotoxin, sterility, and bioburden panels." },
              { t: "Manufacturing", d: "USA-based synthesis with QMS controls." },
              { t: "Purity ≥ 98%", d: "Floor across every compound in the catalog." },
            ].map((q) => (
              <div key={q.t} className="border border-white/10 rounded-md p-6 bg-white/5 backdrop-blur">
                <div className="font-display text-xl font-semibold">{q.t}</div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{q.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Testimonials + Stats */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <SectionHeader eyebrow="06 · Social Proof" title="Trusted by veterinary researchers." description="What professionals say — and the numbers behind the practice." />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {STATS.map((s) => (
            <div key={s.l} className="border border-slate-200 rounded-md p-6 bg-slate-50" data-testid={`stat-${s.l.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="font-display text-3xl font-semibold text-slate-900">{s.n}</div>
              <div className="mt-1 text-sm text-slate-600">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {REVIEWS.map((r, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-6 bg-white card-clinical" data-testid={`review-${i}`}>
              <div className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((s) => <Star key={s} className="w-4 h-4 text-blue-600 fill-blue-600" strokeWidth={1.5} />)}
              </div>
              <p className="mt-4 text-sm text-slate-700 leading-relaxed">"{r.q}"</p>
              <div className="mt-6 pt-4 border-t border-slate-100 text-xs font-mono uppercase tracking-widest text-slate-500">{r.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 10. Final CTA */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="eyebrow mb-4 justify-center">07 · Get Started</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Ready to run <span className="text-blue-600">documented</span> research?
          </h2>
          <p className="mt-6 text-slate-600 text-lg max-w-xl mx-auto">
            Browse the full peptide reference, or open a customer account to track batches and orders.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/catalog">
              <Button data-testid="final-cta-shop" className="h-14 px-7 py-6 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-base">
                Shop Products <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
              </Button>
            </Link>
            <Link to="/login">
              <Button data-testid="final-cta-account" variant="outline" className="h-14 px-7 py-6 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-md font-medium text-base transition-colors">
                Create Account
              </Button>
            </Link>
            <Link to="/contact">
              <Button data-testid="final-cta-contact" variant="ghost" className="h-14 px-7 py-6 text-slate-700 hover:bg-slate-100 rounded-md font-medium text-base">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
